// CB:BEGIN etag-polling-hook
import { useState, useEffect, useRef, useCallback } from 'react';

interface ETagResourceOptions {
  url: string;
  interval?: number; // polling interval in ms, default 15000 (15s)
  enabled?: boolean; // whether polling is enabled, default true
  onUpdate?: (data: any) => void; // callback when data is updated
  onError?: (error: Error) => void; // callback when request fails
}

interface ETagResourceResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => Promise<void>;
  stopPolling: () => void;
  startPolling: () => void;
}

export function useETagResource<T = any>({
  url,
  interval = 15000,
  enabled = true,
  onUpdate,
  onError
}: ETagResourceOptions): ETagResourceResult<T> {
  // CB:BEGIN dev-only-guard
  // Dev-only guard to help detect if the hook module is being evaluated strangely
  if (import.meta.env.DEV) {
    if (typeof window === 'undefined') {
      console.log('[CoreBids] useETagResource evaluated in a non-window environment');
    }
  }
  // CB:END dev-only-guard

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  const etagRef = useRef<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const previousDataRef = useRef<string | null>(null); // CB:BEGIN track-previous-data - Track previous data to detect changes

  // CB:BEGIN fix-tdz-stop-polling
  // Define stopPolling FIRST using a stable ref-based implementation
  // This ensures it's available when startPolling references it
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);
  // CB:END fix-tdz-stop-polling

  const fetchData = useCallback(async (isInitial = false) => {
    try {
      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Add If-None-Match header if we have an ETag (not on initial load)
      if (etagRef.current && !isInitial) {
        headers['If-None-Match'] = etagRef.current;
      }

      const response = await fetch(url, {
        headers,
        signal: abortControllerRef.current.signal,
      });

      // Handle 304 Not Modified
      if (response.status === 304) {
        // Data hasn't changed, update last checked time but don't update data
        setLastUpdated(new Date());
        return;
      }

      if (!response.ok) {
        // CB:BEGIN handle-html-error-responses
        // Check if response is HTML (404 page) instead of JSON
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('text/html')) {
          // This is likely a 404 HTML page, skip silently
          setLoading(false);
          return;
        }
        // CB:END handle-html-error-responses
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Get ETag from response
      const etag = response.headers.get('ETag');
      if (etag) {
        etagRef.current = etag;
      }

      // CB:BEGIN safe-json-parsing
      const text = await response.text();
      let newData;
      try {
        newData = JSON.parse(text);
      } catch (parseError) {
        // If response is HTML or not JSON, skip silently
        if (text.trim().startsWith('<!') || text.trim().startsWith('<html')) {
          setLoading(false);
          return;
        }
        throw parseError;
      }
      // CB:END safe-json-parsing
      
      // CB:BEGIN only-call-onupdate-when-data-changes
      // Only call onUpdate if data actually changed (not on initial load or 304)
      const newDataString = JSON.stringify(newData);
      const dataChanged = previousDataRef.current !== null && previousDataRef.current !== newDataString;
      
      setData(newData);
      previousDataRef.current = newDataString; // Update ref with new data
      setError(null);
      setLastUpdated(new Date());
      setLoading(false);

      // Call update callback only when data actually changed (skip initial load)
      if (onUpdate && dataChanged) {
        onUpdate(newData);
      }
      // CB:END only-call-onupdate-when-data-changes

    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Request was cancelled, don't update state
        return;
      }

      // CB:BEGIN handle-404-gracefully
      // Don't log 404 errors or HTML responses (usually means endpoint doesn't exist)
      const is404 = err.message?.includes('404') || (err as any)?.status === 404;
      const isHTML = err.message?.includes('<!doctype') || err.message?.includes('<html');
      
      if (is404 || isHTML) {
        // Silently skip 404s/HTML responses - project doesn't exist or endpoint missing
        setLoading(false);
        return;
      }
      // CB:END handle-404-gracefully
      console.error('❌ ETag polling error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoading(false);

      if (onError) {
        onError(err instanceof Error ? err : new Error('Unknown error'));
      }
    }
  }, [url, onUpdate, onError]);

  const refetch = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  // CB:BEGIN fix-tdz-start-polling
  // Define startPolling AFTER stopPolling to avoid TDZ
  // Now stopPolling is already defined and can be safely referenced
  const startPolling = useCallback(() => {
    // CB:BEGIN prevent-polling-when-disabled
    // Don't start polling if disabled or URL is empty
    if (!enabled || !url) {
      return;
    }
    // CB:END prevent-polling-when-disabled
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      // Double-check enabled state before each fetch
      if (!enabled || !url) {
        stopPolling();
        return;
      }
      fetchData(false);
    }, interval);
  }, [fetchData, interval, enabled, url, stopPolling]);
  // CB:END fix-tdz-start-polling

  // CB:BEGIN prevent-initial-fetch-when-disabled
  // Initial fetch and setup - only fetch if enabled and URL is valid
  useEffect(() => {
    // Don't fetch if disabled or URL is empty - completely stop all activity
    if (!enabled || !url) {
      setLoading(false);
      stopPolling(); // Ensure polling is stopped
      if (abortControllerRef.current) {
        abortControllerRef.current.abort(); // Cancel any pending requests
      }
      return;
    }

    // Only fetch and start polling if enabled
    fetchData(true);
    startPolling();

    return () => {
      stopPolling();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, startPolling, stopPolling, enabled, url]);
  // CB:END prevent-initial-fetch-when-disabled

  // Handle interval changes
  useEffect(() => {
    if (enabled && intervalRef.current) {
      stopPolling();
      startPolling();
    }
  }, [interval, enabled, stopPolling, startPolling]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    refetch,
    stopPolling,
    startPolling,
  };
}
// CB:END etag-polling-hook
